import { useState } from 'react';
import { useAIAgents } from '@/hooks/useAIAgents';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Activity, Clock, CheckCircle, XCircle, AlertCircle, Play, Eye } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';

export const AgentExecutions = () => {
  const { agents, useAgentExecutions } = useAIAgents();
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedExecution, setSelectedExecution] = useState<any>(null);
  
  const { data: executions = [], isLoading } = useAgentExecutions(
    selectedAgent === 'all' ? undefined : selectedAgent
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running':
        return <Activity className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'default',
      failed: 'destructive',
      running: 'default',
      pending: 'secondary',
      cancelled: 'secondary'
    } as const;

    return (
      <Badge variant={variants[status as keyof typeof variants] || 'secondary'}>
        {status}
      </Badge>
    );
  };

  const getAgentName = (agentId: string) => {
    const agent = agents.find(a => a.id === agentId);
    return agent?.name || 'Unknown Agent';
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-48"></div>
                  <div className="h-3 bg-muted rounded w-32"></div>
                </div>
                <div className="h-6 bg-muted rounded w-20"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <Select value={selectedAgent} onValueChange={setSelectedAgent}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filter by agent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <div className="text-sm text-muted-foreground">
          {executions.length} execution{executions.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Executions List */}
      <div className="space-y-3">
        {executions.map((execution) => (
          <Card key={execution.id} className="hover:shadow-sm transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(execution.status)}
                  <div>
                    <div className="font-medium">
                      {getAgentName(execution.agent_id)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {execution.triggered_by && (
                        <span className="capitalize">{execution.triggered_by}</span>
                      )}
                      {execution.created_at && (
                        <span> • {formatDistanceToNow(new Date(execution.created_at))} ago</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {execution.execution_time_ms && (
                    <div className="text-sm text-muted-foreground">
                      {execution.execution_time_ms}ms
                    </div>
                  )}
                  
                  {execution.cost_estimate && (
                    <div className="text-sm text-muted-foreground">
                      ${(execution.cost_estimate || 0).toFixed(4)}
                    </div>
                  )}

                  {getStatusBadge(execution.status)}

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Execution Details</DialogTitle>
                        <DialogDescription>
                          {getAgentName(execution.agent_id)} • {format(new Date(execution.created_at), 'PPpp')}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium">Status</label>
                            <div className="mt-1">{getStatusBadge(execution.status)}</div>
                          </div>
                          <div>
                            <label className="text-sm font-medium">Triggered By</label>
                            <div className="mt-1 text-sm text-muted-foreground capitalize">
                              {execution.triggered_by || 'Unknown'}
                            </div>
                          </div>
                          {execution.execution_time_ms && (
                            <div>
                              <label className="text-sm font-medium">Execution Time</label>
                              <div className="mt-1 text-sm text-muted-foreground">
                                {execution.execution_time_ms}ms
                              </div>
                            </div>
                          )}
                          {execution.tokens_used && (
                            <div>
                              <label className="text-sm font-medium">Tokens Used</label>
                              <div className="mt-1 text-sm text-muted-foreground">
                                {execution.tokens_used}
                              </div>
                            </div>
                          )}
                          {execution.cost_estimate && (
                            <div>
                              <label className="text-sm font-medium">Cost</label>
                              <div className="mt-1 text-sm text-muted-foreground">
                                ${(execution.cost_estimate || 0).toFixed(4)}
                              </div>
                            </div>
                          )}
                          {execution.started_at && (
                            <div>
                              <label className="text-sm font-medium">Started At</label>
                              <div className="mt-1 text-sm text-muted-foreground">
                                {format(new Date(execution.started_at), 'PPpp')}
                              </div>
                            </div>
                          )}
                          {execution.completed_at && (
                            <div>
                              <label className="text-sm font-medium">Completed At</label>
                              <div className="mt-1 text-sm text-muted-foreground">
                                {format(new Date(execution.completed_at), 'PPpp')}
                              </div>
                            </div>
                          )}
                        </div>

                        {execution.input_data && (
                          <div>
                            <label className="text-sm font-medium">Input Data</label>
                            <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-auto max-h-32">
                              {JSON.stringify(execution.input_data, null, 2)}
                            </pre>
                          </div>
                        )}

                        {execution.output_data && (
                          <div>
                            <label className="text-sm font-medium">Output Data</label>
                            <pre className="mt-1 p-3 bg-muted rounded text-xs overflow-auto max-h-32">
                              {JSON.stringify(execution.output_data, null, 2)}
                            </pre>
                          </div>
                        )}

                        {execution.error_message && (
                          <div>
                            <label className="text-sm font-medium text-red-600">Error Message</label>
                            <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                              {execution.error_message}
                            </div>
                          </div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {execution.error_message && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {execution.error_message}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {executions.length === 0 && !isLoading && (
        <Card>
          <CardContent className="text-center py-12">
            <Activity className="mx-auto h-12 w-12 text-muted-foreground" />
            <h3 className="mt-4 text-lg font-medium">No executions yet</h3>
            <p className="text-muted-foreground">
              Agent executions will appear here once they start running.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};